import React, { useCallback, useState } from 'react';

import { alertError, alertSuccess } from '@dashboard/contexts/SnackbarManager/store';
import {
  type EmailMessagesExportQuery,
  type EmailMessagesFilterInput,
  useEmailMessagesExportQuery,
} from '@dashboard/generated/wp/platform';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';

const EXPORT_PAGE_SIZE = 500;

type ExportEmailMessage = EmailMessagesExportQuery['EmailMessages']['nodes'][number];

type Props = {
  filter: EmailMessagesFilterInput;
  disabled?: boolean;
};

const COLUMNS = [
  ['id', (message: ExportEmailMessage) => message.id],
  ['created_at', (message: ExportEmailMessage) => message.createdAt],
  ['updated_at', (message: ExportEmailMessage) => message.updatedAt],
  ['processed_at', (message: ExportEmailMessage) => message.processedAt],
  ['status', (message: ExportEmailMessage) => message.status],
  ['source_key', (message: ExportEmailMessage) => message.source],
  ['from', (message: ExportEmailMessage) => message.message.from],
  ['to', (message: ExportEmailMessage) => message.message.to],
  ['cc', (message: ExportEmailMessage) => message.message.cc],
  ['bcc', (message: ExportEmailMessage) => message.message.bcc],
  ['reply_to', (message: ExportEmailMessage) => message.message.replyTo],
  ['subject', (message: ExportEmailMessage) => message.message.subject],
  ['meta_id', (message: ExportEmailMessage) => message.metaId],
  ['metadata', (message: ExportEmailMessage) => message.metadata],
  ['waypoint_template_id', (message: ExportEmailMessage) => message.waypointTemplateId],
  ['source_workflow_run_id', (message: ExportEmailMessage) => message.sourceWorkflowRunId],
  ['source_email_messages_batch_id', (message: ExportEmailMessage) => message.sourceEmailMessagesBatchId],
  ['contacts_group_id', (message: ExportEmailMessage) => message.contactsGroupId],
] as const;

export default function ExportMessagesButton({ filter, disabled }: Props) {
  const { refetch: refetchEmailMessagesExport } = useEmailMessagesExportQuery({
    fetchPolicy: 'network-only',
    skip: true,
    variables: { page: 1, limit: EXPORT_PAGE_SIZE, filter },
  });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const onClick = useCallback(async () => {
    setExporting(true);
    setExportProgress(0);
    try {
      const { data: firstData } = await refetchEmailMessagesExport({ page: 1, limit: EXPORT_PAGE_SIZE, filter });
      if (!firstData) {
        throw new Error('Email messages export query returned no data.');
      }

      const count = firstData.EmailMessages.count;
      const messages: ExportEmailMessage[] = [...firstData.EmailMessages.nodes];
      const totalPages = Math.ceil(count / EXPORT_PAGE_SIZE);
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

      let pagesLoaded = 1;
      setExportProgress(Math.round((pagesLoaded / totalPages) * 100));

      for (let i = 0; i < remainingPages.length; i += 5) {
        const batch = remainingPages.slice(i, i + 5);
        const results = await Promise.all(
          batch.map((page) => refetchEmailMessagesExport({ page, limit: EXPORT_PAGE_SIZE, filter }))
        );
        for (const { data } of results) {
          if (!data) {
            throw new Error('Email messages export query returned no data.');
          }
          messages.push(...data.EmailMessages.nodes);
        }
        pagesLoaded += batch.length;
        setExportProgress(Math.round((pagesLoaded / totalPages) * 100));
      }

      downloadCsv({
        filename: `waypoint-messages-${new Date().toISOString().slice(0, 10)}.csv`,
        rows: messages,
      });
      alertSuccess(`Exported ${new Intl.NumberFormat('en-US').format(messages.length)} messages to a CSV file`);
    } catch (error) {
      alertError('There were errors exporting messages');
    } finally {
      setExporting(false);
    }
  }, [filter, refetchEmailMessagesExport]);

  return (
    <Button
      color="secondary"
      disabled={disabled || exporting}
      onClick={onClick}
      size="small"
      startIcon={<FileDownloadOutlined />}
      sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
    >
      {exporting ? `Exporting... ${exportProgress}%` : 'Export CSV'}
    </Button>
  );
}

function downloadCsv({ filename, rows }: { filename: string; rows: ExportEmailMessage[] }) {
  const csv = buildCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(href);
}

function buildCsv(rows: ExportEmailMessage[]): string {
  const header = COLUMNS.map(([name]) => serializeCsvValue(name)).join(',');
  const body = rows.map((row) => COLUMNS.map(([, getValue]) => serializeCsvValue(getValue(row))).join(','));
  return [header, ...body].join('\n');
}

function serializeCsvValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  let stringValue: string;
  if (value instanceof Date) {
    stringValue = value.toISOString();
  } else if (typeof value === 'string') {
    stringValue = value;
  } else {
    stringValue = JSON.stringify(value);
  }
  return `"${stringValue.replace(/"/g, '""')}"`;
}
